/**
 * Пустые состояния и загрузка — единым видом на всех страницах.
 *
 * Раньше «нечего показать» выглядело по-разному в каждом месте: где-то серый
 * <p> без рамки, где-то центрированный текст в карточке, где-то строка таблицы
 * с p-6. Пользователь читает это как разные ситуации, хотя ситуация одна.
 * Отличие пустоты от загрузки тоже важно: «данных нет» и «данные ещё едут» —
 * разные новости, и раньше их путали, потому что обе рисовались одинаковым
 * «Загрузка…».
 */
import React from 'react';
import { cn } from './cn';
import i18n from '../i18n';

/** Ничего нет. `action` — кнопка, которая эту пустоту устраняет. */
export const EmptyState: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  /** compact — внутри уже существующей карточки, без своей рамки. */
  compact?: boolean;
}> = ({ title, description, icon, action, className, compact }) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center gap-2 px-6 text-center',
      compact ? 'py-8' : 'rounded-xl border border-dashed border-border py-12',
      className,
    )}
  >
    {icon && <div className="text-muted-foreground/50 [&_svg]:size-7">{icon}</div>}
    <div className="text-base font-medium text-foreground">{title}</div>
    {description && <p className="max-w-md text-sm text-muted-foreground">{description}</p>}
    {action && <div className="mt-1">{action}</div>}
  </div>
);

/** Данные едут. Три точки вместо спиннера: блок обычно маленький, а
 *  крутилка в списке из шести карточек создаёт ощущение, что всё сломалось. */
export const LoadingState: React.FC<{ label?: string; className?: string; compact?: boolean }> = ({
  label = i18n.t('state:loading'),
  className,
  compact,
}) => (
  <div
    className={cn(
      'flex items-center justify-center gap-2 text-sm text-muted-foreground',
      compact ? 'py-6' : 'py-12',
      className,
    )}
  >
    <span className="loading-dots">
      <span className="loading-dot" />
      <span className="loading-dot" />
      <span className="loading-dot" />
    </span>
    <span>{label}…</span>
  </div>
);

/**
 * То же самое, но строкой внутри <tbody>. Отдельный компонент нужен потому,
 * что <div> между <tbody> и <tr> браузер выкидывает из таблицы, и сообщение
 * уезжало вверх страницы.
 */
export const TableState: React.FC<{
  colSpan: number;
  loading?: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  loadingLabel?: string;
}> = ({ colSpan, loading, title, description, loadingLabel }) => (
  <tr>
    <td colSpan={colSpan} className="px-4">
      {loading
        ? <LoadingState compact label={loadingLabel} />
        : <EmptyState compact title={title} description={description} />}
    </td>
  </tr>
);

/** Ошибка загрузки. Отдельно от пустоты: пустой список — норма,
 *  а упавший запрос требует другого цвета и другого действия. */
export const ErrorState: React.FC<{ message: React.ReactNode; className?: string }> = ({ message, className }) => (
  <div
    className={cn(
      'rounded-md border border-trade-red/40 bg-trade-red/10 px-3 py-2 text-sm text-trade-red',
      className,
    )}
  >
    {message}
  </div>
);
